import { useSessionStore } from './useSessionStore'
import { useUIStore } from './useUIStore'
import { useCartStore } from './useCartStore'
import { useFaqStore } from './useFaqStore'
import { SessionResult } from '@/constants'
import { isPreviewSession } from '@/utils/session'

class IdleTimerManager {
  private timerId: number | null = null
  private warningTimerId: number | null = null
  private warningMessageId: string | null = null

  start() {
    this.clear()

    const session = useSessionStore.getState().session as any
    const timeoutSecs = session?.lifeline_timeout
    // A preview is a rendering of the funnel, not a session anyone is using: no idle
    // expiry, no expiry warning, no lifeline.
    if (!session || !session.can_enter_lifeline || !timeoutSecs || timeoutSecs <= 0 || isPreviewSession(session)) {
      return
    }
    // The lifeline is already on screen; re-arming would trigger it again in a loop.
    // Closing it calls resetIdleTimer, which starts the clock over.
    if (session.is_lifeline_active || session.show_lifeline_contact) {
      return
    }

    const timeoutMs = timeoutSecs * 1000
    const warningOffsetMs = Math.max(timeoutMs - 5 * 60 * 1000, timeoutMs * 0.8)

    this.warningTimerId = window.setTimeout(() => {
      this.showIdleWarning()
    }, warningOffsetMs)

    this.timerId = window.setTimeout(() => {
      this.handleIdleTimeout()
    }, timeoutMs)
  }

  reset() {
    this.start()
  }

  clear() {
    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
    if (this.warningTimerId) {
      clearTimeout(this.warningTimerId)
      this.warningTimerId = null
    }
    this.dismissIdleWarning()
  }

  private showIdleWarning() {
    this.warningMessageId =
      useUIStore.getState().addUIMessage({
        type: 'warning',
        message: 'Your session is about to expire. Move your mouse or tap the screen to stay signed in.',
        duration: 30000,
      }) ?? null
  }

  private dismissIdleWarning() {
    if (this.warningMessageId) {
      useUIStore.getState().removeUIMessage(this.warningMessageId)
      this.warningMessageId = null
    }
  }

  private handleIdleTimeout() {
    this.dismissIdleWarning()
    const live = useSessionStore.getState().session as any
    if (live?.is_lifeline_active || live?.show_lifeline_contact) {
      return
    }
    const uiStore = useUIStore.getState()
    const lifelineCallback = uiStore.lifelineCallback
    if (typeof lifelineCallback === 'function') {
      lifelineCallback(SessionResult.LifelineTimeout, 'Session expired due to inactivity')
    } else {
      // No callback wired — fall back to plain logout
      useSessionStore.getState().logout()
      uiStore.addUIMessage({
        type: 'warning',
        message: 'Your session has expired due to inactivity. Please start a new session.',
      })
    }
  }
}

// Global idle timer instance
const idleTimer = new IdleTimerManager()

const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

// Module-scoped handler reference so add/remove pair the same function.
// Without this, each call creates a fresh closure and removeEventListener
// can never match the listener attached by addEventListener.
const resetIdleTimerOnActivity = () => {
  if (useSessionStore.getState().isSessionActive()) {
    idleTimer.reset()
  }
}

let activityListenersAttached = false

function addActivityListeners() {
  if (typeof window === 'undefined' || activityListenersAttached) {
    return
  }
  activityListenersAttached = true
  activityEvents.forEach(event => {
    document.addEventListener(event, resetIdleTimerOnActivity, true)
  })
}

function removeActivityListeners() {
  if (typeof window === 'undefined' || !activityListenersAttached) {
    return
  }
  activityListenersAttached = false
  activityEvents.forEach(event => {
    document.removeEventListener(event, resetIdleTimerOnActivity, true)
  })
}

// Lifeline callback system
export interface LifelineCallbackParams {
  sessionResult: SessionResult
  message: string
  errorData?: any
}

export type LifelineCallback = (result: SessionResult, message: string, errorData?: any) => void

export function setLifelineCallback(callback: LifelineCallback) {
  useUIStore.getState().setLifelineCallback(callback)
}

export function clearLifelineCallback() {
  useUIStore.getState().setLifelineCallback(null)
}

// Background data loading
export async function loadBackgroundData() {
  const faqStore = useFaqStore.getState()

  try {
    // Load FAQ topics in background
    await faqStore.loadFaqTopics()
  } catch {
    // Don't throw - this is background loading
  }

  const session = useSessionStore.getState().session as any
  if (session?.customer) {
    try {
      await useSessionStore.getState().getCart()
    } catch {
      /* background — ignore */
    }
  }
}

// Session event handling
export function onSessionStarted(authData: { auth_token: string; session: any }) {
  const sessionStore = useSessionStore.getState()
  const uiStore = useUIStore.getState()

  // Set authentication data
  sessionStore.setAuthSuccess(authData.auth_token)
  sessionStore.setSession(authData.session)

  // Load background data
  loadBackgroundData()

  // Start idle timer
  idleTimer.start()

  // Clear any errors
  uiStore.clearError()
  uiStore.ready()
}

// Store initialization
export function initializeStores() {
  // Add activity listeners for idle detection
  addActivityListeners()

  // Check if we have an existing session and start idle timer + lifeline
  const sessionStore = useSessionStore.getState()
  if (sessionStore.isSessionActive()) {
    idleTimer.start()
    // Set up lifeline callback for existing sessions (page reload)
    setLifelineCallback((_result: SessionResult, _message: string) => {
      const store = useSessionStore.getState()
      if (isPreviewSession(store.session)) {
        return
      }
      store.updateSession({ result: String(_result) }).catch(() => {})
      store.logout()
    })
  }

  // Initialize UI state
  const uiStore = useUIStore.getState()
  uiStore.setInitialLoading(false)

  // Check for IE browser
  if (typeof window !== 'undefined') {
    const isIE = !!window.navigator.userAgent.match(/(MSIE|Trident)/)
    uiStore.setIE(isIE)
  }
}

// Store cleanup
export function cleanupStores() {
  // Clear idle timer
  idleTimer.clear()

  // Remove activity listeners
  removeActivityListeners()

  // Clear any pending callbacks
  clearLifelineCallback()
}

// Idle timer controls (for manual management)
export const idleTimerControls = {
  start: () => idleTimer.start(),
  reset: () => idleTimer.reset(),
  clear: () => idleTimer.clear(),
}

// Store persistence utilities
export function clearPersistedState() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    // Zustand persist slices — must all clear together so a re-login starts blank.
    localStorage.removeItem('ivrpath')
    localStorage.removeItem('ivrpath-chatbot')
    localStorage.removeItem('ivrpath-cart')
    localStorage.removeItem('ivrpath-faqs')
    localStorage.removeItem('ivrpath-ui')

    // Per-feature keys that hold user-specific data.
    localStorage.removeItem('chat')
    localStorage.removeItem('processing_gif')
    localStorage.removeItem('orderDetail')
    localStorage.removeItem('customer_support')
    localStorage.removeItem('react-query-cache')
    sessionStorage.removeItem('orderDetail')
    sessionStorage.removeItem('checkout-customer')
    sessionStorage.removeItem('checkout-card')
  } catch {
    // Storage may be unavailable; the reset is best-effort.
  }
}

// Migration utilities (for React state management)
export function migrateLegacyState() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    // Try to migrate from old persistence
    const legacyState = localStorage.getItem('ivrpath')
    if (legacyState) {
      const parsedState = JSON.parse(legacyState)

      // Migrate session data if available
      if (parsedState.session || parsedState.token) {
        const sessionStore = useSessionStore.getState()
        if (parsedState.token) {
          sessionStore.setAuthSuccess(parsedState.token)
        }
        if (parsedState.session) {
          sessionStore.setSession(parsedState.session)
        }
      }

      // Migrate cart data if available
      if (parsedState.cart) {
        const cartStore = useCartStore.getState()
        cartStore.setCart(parsedState.cart)
      }

      // Migrate FAQ data if available
      if (parsedState.allFaqTopics || parsedState.votedFaqs) {
        const faqStore = useFaqStore.getState()
        if (parsedState.allFaqTopics) {
          faqStore.setFaqTopics(parsedState.allFaqTopics)
        }
        if (parsedState.votedFaqs) {
          // Migrate voted FAQs
          Object.entries(parsedState.votedFaqs).forEach(([faqId, vote]) => {
            faqStore.voteFaq(Number(faqId), vote as 'up' | 'down')
          })
        }
      }

      // Migrate UI settings if available
      if (parsedState.styleVariables || parsedState.debug || parsedState.currentLanguage) {
        const uiStore = useUIStore.getState()
        if (parsedState.styleVariables) {
          uiStore.setStyleVariables(parsedState.styleVariables)
        }
        if (parsedState.debug !== undefined) {
          uiStore.setDebug(parsedState.debug)
        }
      }
    }
  } catch {
    // A malformed persisted blob must not block startup.
  }
}
